import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Heading, Text } from 'react-aria-components';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import Tag from 'src/components/ui/Tag';
import InstitutionField from 'src/components/auth/InstitutionField';
import OrganizationField from 'src/components/auth/OrganizationField';
import { graphQLErrorNotification } from 'src/utils/notifications';
import MailIcon from 'src/assets/icons/envelope.svg';
import ArrowRightIcon from 'src/assets/icons/arrow-right-1.svg';
import MAGIC_LOGIN_MUTATION from 'src/graphql/mutation/magic-login.graphql';
import { ENV_MODE, isWhiteLabel } from 'src/utils';
import ORGANIZATIONS_LIST from 'src/graphql/query/organizations-list.graphql';
import INSTITUTION_BY_ID from 'src/graphql/query/institution-by-id.graphql';

const CreatorRegisterPage = () => {
  useEffect(() => {
    document.body.classList.add('login');

    return () => {
      document.body.classList.remove('login');
    };
  }, []);

  return <RegisterForm />;
};

const RegisterForm = () => {
  const navigate = useNavigate();
  const {
    organizationId: organizationIdParam,
    institutionId: institutionIdParam,
  } = Route.useSearch();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const validationSchema = useMemo(
    () => buildValidationSchema(organizationIdParam, institutionIdParam),
    [organizationIdParam, institutionIdParam]
  );

  const methods = useForm<RegisterSchemaType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      email: '',
      organizationId: '',
      institutionId: '',
    },
  });

  const { handleSubmit, clearErrors, reset, control, watch } = methods;

  const formOrganizationId = watch('organizationId');

  // The active org is the URL param (fixed) or the form selection
  const activeOrganizationId =
    organizationIdParam ??
    (formOrganizationId ? Number(formOrganizationId) : undefined);

  const showInstitutionDropdown =
    Boolean(activeOrganizationId) && !institutionIdParam;

  const { data: orgsData } = useQuery(ORGANIZATIONS_LIST);

  const { data: institutionData } = useQuery(INSTITUTION_BY_ID, {
    variables: { id: String(institutionIdParam) },
    skip: !institutionIdParam,
  });

  const orgName = orgsData?.organizationsList?.find(
    (o: { organizationId: string }) =>
      Number(o.organizationId) === organizationIdParam
  )?.name;

  const institutionName = institutionData?.institution?.name;

  const [magicLogin, { loading }] = useMutation(MAGIC_LOGIN_MUTATION, {
    onCompleted: async (data, clientOptions) => {
      if (data.magicLogin) {
        const email = clientOptions?.variables?.input?.email;

        await navigate({
          from: '/creator-register',
          to: '/magic-link',
          replace: true,
          search: { email, from: 'creator-register' },
        });
      }
    },
    onError: graphQLErrorNotification,
  });

  const onSubmit = useCallback(
    async ({ email = '', organizationId = '', institutionId = '' }) => {
      const recaptcha = executeRecaptcha
        ? await executeRecaptcha('register')
        : null;
      const input = { email, recaptcha };
      const scope = {
        organizationId: organizationIdParam ?? (Number(organizationId) || null),
        institutionId: Number(institutionIdParam ?? institutionId) || null,
      };

      clearErrors();
      await magicLogin({
        variables: {
          input,
          role: 'CREATOR',
          scope,
          whitelabelKey: isWhiteLabel ? ENV_MODE : null,
        },
      });
      reset();
    },
    [
      magicLogin,
      clearErrors,
      reset,
      executeRecaptcha,
      organizationIdParam,
      institutionIdParam,
    ]
  );

  return (
    <div>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Join the Creator Studio!
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
      >
        Enter your email address to create your account.
      </Text>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {organizationIdParam ? (
            <ReadOnlyField
              label="Organization"
              value={orgName ?? String(organizationIdParam)}
            />
          ) : (
            <OrganizationField
              name="organizationId"
              className="mt-4"
              isRequired
            />
          )}
          {institutionIdParam ? (
            <ReadOnlyField
              label="Institution"
              value={institutionName ?? String(institutionIdParam)}
            />
          ) : (
            showInstitutionDropdown && (
              <InstitutionField
                name="institutionId"
                organizationId={String(activeOrganizationId)}
                className="mt-4"
                isRequired
              />
            )
          )}
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { invalid, error } }) => (
              <TextField
                {...field}
                Icon={MailIcon}
                label="Email"
                className="mt-4"
                placeholder="john@gmail.com"
                validationBehavior="aria"
                isInvalid={invalid}
                errorMessage={error?.message}
                isRequired
                autoComplete="email"
              />
            )}
          />
          <Button
            className="w-full justify-center mt-8 gap-2"
            type="submit"
            isDisabled={loading}
            loading={loading}
          >
            Continue
            <ArrowRightIcon className="w-3 h-3 text-primary-45" />
          </Button>
        </form>
      </FormProvider>
    </div>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div className="my-4">
    <Text className="block font-bold text-surface-alt-75">{value}</Text>
    <Tag title={label} />
  </div>
);

const buildValidationSchema = (
  organizationIdParam: number | undefined,
  institutionIdParam: number | undefined
) =>
  z
    .object({
      email: z.string().email(),
      organizationId: organizationIdParam
        ? z.string().optional()
        : z.string().min(1, 'Required'),
      institutionId: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (institutionIdParam) return;
      const activeOrgId =
        organizationIdParam ??
        (data.organizationId ? Number(data.organizationId) : undefined);
      if (activeOrgId && !data.institutionId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: ['institutionId'],
        });
      }
    });

type RegisterSchemaType = z.infer<ReturnType<typeof buildValidationSchema>>;

const RegisterSearchSchema = z.object({
  organizationId: z.number().optional(),
  institutionId: z.number().optional(),
});

export const Route = createFileRoute('/_auth/creator-register')({
  validateSearch: (search) => RegisterSearchSchema.parse(search),
  component: CreatorRegisterPage,
});
